import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { login } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Users, Eye, Shield, Loader2, Sparkles, Zap } from 'lucide-react';
import { DiamondLogo } from '../components/shared/DiamondLogo';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import TestimonialsCarousel from '../components/shared/TestimonialsCarousel';
import PromoBanner from '../components/shared/PromoBanner';
import ReferralBanner from '../components/shared/ReferralBanner';
import { trackInstallEvent } from '../lib/analytics';
import { capturePromoFromUrl } from '../lib/promoLink';
import { captureRefFromUrl } from '../lib/refLink';

/** Capture & store UTM/ref source from the URL — used for marketing attribution. */
const SIGNUP_SOURCE_KEY = 'ds_signup_source_v1';
function captureLandingSource() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw =
      params.get('utm_source') ||
      params.get('utm_medium') ||
      params.get('ref') ||
      params.get('src') ||
      params.get('via') ||
      null;
    if (raw) {
      const campaign = params.get('utm_campaign');
      const value = campaign ? `${raw}:${campaign}` : raw;
      window.localStorage.setItem(SIGNUP_SOURCE_KEY, value.slice(0, 80));
    }
  } catch { /* ignore */ }
}
import { toast } from 'sonner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isCoach, isAdmin, logout, setAuth, registeredAs } = useAuthStore();
  const [showAdminBox, setShowAdminBox] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  // Auto-redirect authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      if (registeredAs === 'parent' && !isCoach && !isAdmin) {
        navigate('/parent-browse', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated]);

  // Analytics: track first view
  useEffect(() => { trackInstallEvent('page_view', { page: 'landing' }); }, []);

  // Capture ?promo=CODE from URL into localStorage so register + checkout auto-apply it
  useEffect(() => { capturePromoFromUrl(); }, []);

  // Capture ?ref=CODE so register flow knows who invited this user (+ they get 30-day trial)
  useEffect(() => { captureRefFromUrl(); }, []);

  // Capture marketing source (?utm_source / ?ref / etc) for attribution analytics
  useEffect(() => { captureLandingSource(); }, []);

  const handleCoachClick = () => {
    navigate('/login?role=coach');
  };

  const handleParentClick = () => {
    navigate('/login?role=parent');
  };

  const handleAdminLogin = async () => {
    if (!adminPassword.trim()) {
      toast.error('Please enter password');
      return;
    }
    
    setAdminLoading(true);
    try {
      // Try to login with admin credentials (Toddro account)
      const response = await login('Toddro', adminPassword);
      if (response.user?.is_admin) {
        setAuth(response.user, response.token);
        toast.success('Admin access granted!');
        navigate('/dashboard');
      } else {
        toast.error('Invalid admin credentials');
      }
    } catch (error) {
      toast.error('Invalid admin credentials');
    } finally {
      setAdminLoading(false);
      setAdminPassword('');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'radial-gradient(ellipse at top, #131E36 0%, #0A1322 100%)',
      }}
      data-testid="landing-page"
    >
      <div className="max-w-2xl w-full space-y-8">
        {/* Logo and Title — using the official 3D DS monogram */}
        <div className="text-center">
          <div className="inline-block mb-6 relative">
            <img
              src="/diamond-scorer-logo.jpg"
              alt="Diamond Scorer"
              width={120}
              height={120}
              style={{
                borderRadius: 22,
                boxShadow: '0 0 32px rgba(46,190,107,0.55), 0 4px 16px rgba(0,0,0,0.5)',
                objectFit: 'cover',
              }}
            />
          </div>
          <h1 
            className="text-4xl md:text-6xl font-black mb-3 leading-none tracking-tight"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '-0.02em' }}
          >
            <span style={{ color: '#2EBE6B' }}>DIAMOND</span>
            <span style={{ color: '#F5C455' }} className="italic"> SCORER</span>
          </h1>
          <p className="text-slate-300 text-base md:text-lg tracking-wide">
            The baseball &amp; softball scoring app that&apos;s built for game day.
          </p>
        </div>

        {/* Install App Prompt — auto-detects device */}
        <InstallAppPrompt variant="banner" className="max-w-2xl mx-auto" />

        {/* Captured referral (e.g. /?ref=COACH-XXXX) — confirms 30-day trial */}
        <ReferralBanner className="max-w-2xl mx-auto" />

        {/* Captured promo (e.g. from a blog link) — auto-applied at signup */}
        <PromoBanner />

        {/* REAL scoring video hero — 8 sec of actual game-day footage with music */}
        <div className="space-y-3" data-testid="scoring-hero">
          <div className="relative mx-auto w-full max-w-[320px]">
            {/* Phone-frame style container */}
            <div className="rounded-[28px] overflow-hidden shadow-2xl border-4 border-green-950 bg-stone-900 relative">
              <video
                src="/marketing/scoring_demo_hero.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto block"
                data-testid="scoring-hero-video"
                aria-label="Diamond Scorer scoring demo video"
              />
              {/* Live indicator */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-red-600/90 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live
              </div>
            </div>
            {/* Drop shadow glow behind phone */}
            <div className="absolute inset-0 -z-10 bg-amber-500/20 blur-3xl rounded-full scale-110" aria-hidden="true" />
          </div>
          <p className="text-center text-green-200/80 text-xs">
            Real coach scoring a real game. Tap sound on.
          </p>
        </div>

        {/* Feature pills — social proof of what the video just showed */}
        <div className="flex flex-wrap justify-center gap-2 -mt-2">
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20"><Zap size={12} className="text-amber-300" /> Tap-to-score</span>
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20"><Eye size={12} className="text-amber-300" /> Live parent feed</span>
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20"><Sparkles size={12} className="text-amber-300" /> AI lineup + stats</span>
        </div>

        {/* Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Coach Card */}
          <Card 
            className="border-0 shadow-xl cursor-pointer transition-all hover:scale-105 hover:shadow-2xl bg-white"
            onClick={handleCoachClick}
            data-testid="coach-entry-btn"
          >
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={32} />
              </div>
              <h2 
                className="text-2xl font-bold text-green-900 mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                I'M A COACH
              </h2>
              <p className="text-stone-600 text-sm">
                Manage lineups, track stats, and run your team like a pro
              </p>
              <Button 
                className="mt-4 bg-green-900 hover:bg-green-800 w-full"
              >
                {isAuthenticated && (isCoach || isAdmin) ? 'Go to Dashboard' : 'Login / Register'}
              </Button>
            </CardContent>
          </Card>

          {/* Parent Card */}
          <Card 
            className="border-0 shadow-xl cursor-pointer transition-all hover:scale-105 hover:shadow-2xl bg-white"
            onClick={handleParentClick}
            data-testid="parent-entry-btn"
          >
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="text-white" size={32} />
              </div>
              <h2 
                className="text-2xl font-bold text-amber-600 mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                I'M A PARENT
              </h2>
              <p className="text-stone-600 text-sm">
                View live game scores and see who's on the field
              </p>
              <Button 
                className="mt-4 bg-amber-500 hover:bg-amber-600 w-full"
              >
                {isAuthenticated ? 'Browse Teams' : 'Login / Register'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Scan Card feature — matches the proven 30k+ view FB ad */}
        <div className="relative bg-gradient-to-br from-green-950/80 to-green-900/60 border border-green-700/40 rounded-3xl overflow-hidden" data-testid="scan-card-highlight">
          <div className="flex flex-col md:flex-row items-stretch">
            {/* Left column: copy */}
            <div className="flex-1 p-6 md:p-8 text-left">
              <div className="inline-flex items-center gap-1.5 bg-green-600 text-white text-[11px] font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
                Fan favorite · 30K+ seen on Facebook
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                SCORE <span className="text-amber-400">SMARTER</span>,<br />
                NOT HARDER.
              </h3>
              <p className="text-green-100 text-sm md:text-base mb-4">
                Got a handwritten lineup card? <span className="font-semibold text-amber-300">Snap a photo.</span> Diamond Scorer digitizes the whole batting order in seconds — no typing, no setup, ready to score the moment you hit the field.
              </p>
              {/* Step chips */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white/5 rounded-lg px-2 py-3 border border-white/10">
                  <div className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">Step 1</div>
                  <div className="text-white text-xs font-semibold mt-0.5">Lineup Card</div>
                </div>
                <div className="bg-white/5 rounded-lg px-2 py-3 border border-white/10">
                  <div className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">Step 2</div>
                  <div className="text-white text-xs font-semibold mt-0.5">Snap a Photo</div>
                </div>
                <div className="bg-white/5 rounded-lg px-2 py-3 border border-white/10">
                  <div className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">Step 3</div>
                  <div className="text-white text-xs font-semibold mt-0.5">Instant Lineup</div>
                </div>
              </div>
            </div>
            {/* Right column: the actual FB ad image */}
            <div className="md:w-[280px] flex-shrink-0 bg-green-950/50 flex items-center justify-center p-4">
              <img
                src="/marketing/scan_card_ad.jpg"
                alt="Scan Card feature — lineup card to photo to instant lineup"
                className="w-full max-w-[240px] h-auto rounded-xl shadow-2xl"
                data-testid="scan-card-ad-image"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* AI Lineup feature highlight */}
        <div className="relative bg-gradient-to-br from-amber-500/10 via-amber-400/5 to-transparent border border-amber-500/30 rounded-3xl p-6 md:p-8 mt-2 backdrop-blur-sm" data-testid="ai-lineup-highlight">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            {/* Left: copy */}
            <div className="flex-1 text-left">
              <div className="inline-flex items-center gap-1.5 bg-amber-500 text-stone-900 text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                <Sparkles size={12} /> NEW · AI POWERED
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                STOP GUESSING YOUR<br />
                <span className="text-amber-400">BATTING ORDER.</span>
              </h3>
              <p className="text-green-100 text-sm md:text-base mb-4">
                Diamond Scorer's AI reads your team's stats — OBP, slugging, RBIs, contact rate — and builds a 9-spot batting order in seconds. <span className="text-amber-300 font-semibold">With reasoning for every pick.</span>
              </p>
              {/* Sample reasoning quote */}
              <div className="bg-white/10 border-l-4 border-amber-400 px-3 py-2 rounded text-xs text-green-50 italic mb-4">
                "RUSSELL provides power and RBI strength, ideal for the third spot."
                <div className="not-italic text-amber-300 text-[10px] mt-1 font-semibold">— AI Lineup, on a real coach's team</div>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="bg-green-700/60 text-green-50 px-2.5 py-1 rounded-full"><Zap size={10} className="inline mr-1" />5-second build</span>
                <span className="bg-green-700/60 text-green-50 px-2.5 py-1 rounded-full">Reads every at-bat logged</span>
                <span className="bg-green-700/60 text-green-50 px-2.5 py-1 rounded-full">Tweak before applying</span>
              </div>
            </div>
            {/* Right: phone mockup video */}
            <div className="w-full md:w-[280px] flex-shrink-0">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-green-800/40 bg-stone-50">
                <video
                  src="/marketing/ai_lineup_demo.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto block"
                  data-testid="ai-lineup-demo-video"
                />
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-green-300 text-sm">
          ⚾ Baseball & 🥎 Softball • Made for Coaches
        </p>

        {/* Testimonials Carousel (renders only when published testimonials exist) */}
        <TestimonialsCarousel />

        {/* Admin Access Box */}
        <div className="text-center">
          {!showAdminBox ? (
            <Button 
              variant="ghost" 
              size="sm"
              className="text-green-400/50 hover:text-green-300 text-xs"
              onClick={() => setShowAdminBox(true)}
              data-testid="admin-toggle-btn"
            >
              <Shield size={12} className="mr-1" /> Admin
            </Button>
          ) : (
            <div className="bg-black/20 rounded-lg p-4 max-w-xs mx-auto space-y-3">
              <div className="flex items-center justify-center gap-2 text-green-300 text-sm">
                <Shield size={16} />
                <span>Admin Access</span>
              </div>
              <Input
                type="password"
                placeholder="Admin password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="bg-white/10 border-green-500/30 text-white placeholder:text-green-300/50"
                data-testid="admin-password-input"
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-green-300 hover:text-white"
                  onClick={() => {
                    setShowAdminBox(false);
                    setAdminPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleAdminLogin}
                  disabled={adminLoading}
                  data-testid="admin-login-btn"
                >
                  {adminLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enter'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Logout if authenticated */}
        {isAuthenticated && (
          <div className="text-center">
            <Button 
              variant="ghost" 
              className="text-green-200 hover:text-white"
              onClick={logout}
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        )}

        {/* Legal Footer — required for App Store submission */}
        <div className="text-center pt-8 pb-2">
          <div className="flex items-center justify-center gap-4 text-[11px] text-green-300/60 flex-wrap">
            <a href="/privacy" className="hover:text-green-200 underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy Policy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-green-200 underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms of Use</a>
            <span>·</span>
            <a href="mailto:support@diamondscorer.com" className="hover:text-green-200" data-testid="footer-support-link">Support</a>
            <span>·</span>
            <a href="/contact" className="hover:text-green-200 underline-offset-2 hover:underline" data-testid="footer-contact-link">Contact</a>
            <span>·</span>
            <a href="/blog" className="hover:text-green-200 underline-offset-2 hover:underline" data-testid="footer-blog-link">Blog</a>
            <span>·</span>
            <a href="/league-partners" className="hover:text-amber-300 font-semibold underline-offset-2 hover:underline" data-testid="footer-league-link">For League Commissioners</a>
          </div>
          <p className="text-[10px] text-green-400/40 mt-2">Diamond Scorer &copy; {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}
